import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ChevronLeft,
  ChevronRight,
  Inbox,
  Mail,
  MailOpen,
  Paperclip,
  PenSquare,
  RefreshCw,
  Search,
  Star,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  MAILER_FOLDERS,
  type MailerFolder,
  type MailerStatus,
  type MailerThreadAction,
  type MailerThreadSummary,
} from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { useAuth } from '@/lib/auth';
import { usePollingInterval } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input, Select } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/feedback';
import { PageHeader } from '@/components/common/page';
import { Composer } from '@/features/mail/composer';
import { listDate, withSignature } from '@/features/mail/compose-utils';
import { cn } from '@/lib/utils';

type ThreadsResponse = {
  data: MailerThreadSummary[];
  meta: { nextPageToken: string | null; resultSizeEstimate: number };
};

const FOLDER_IDS = MAILER_FOLDERS.map((f) => f.id) as string[];

export const useMailerStatus = () =>
  useQuery({
    queryKey: qk.mailerStatus,
    queryFn: () => api.get<SingleResponse<MailerStatus>>('/mailer/status'),
    refetchInterval: usePollingInterval(true, 60_000),
  });

/** 未接続のときの案内。管理者には設定画面への導線を出す */
export const MailerNotConnected = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  return (
    <EmptyState
      title="Google Workspace が未接続です"
      description={
        hasRole('admin')
          ? '設定 → Google Workspace で OAuth クライアントを登録し、メーラーで使う受信箱を接続してください'
          : '管理者に、設定 → Google Workspace で受信箱を接続するよう依頼してください'
      }
      action={hasRole('admin') ? { label: '接続を設定する', onClick: () => navigate('/settings/google-workspace') } : undefined}
      icon={<Mail className="size-8" />}
    />
  );
};

/** S52 メーラー（Gmail）。スレッド単位の一覧とラベル */
export const MailPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const folderParam = searchParams.get('folder') ?? 'INBOX';
  const folder = (FOLDER_IDS.includes(folderParam) ? folderParam : 'INBOX') as MailerFolder;
  const q = searchParams.get('q') ?? '';
  const [search, setSearch] = useState(q);
  /** ページ送りに使う pageToken の履歴（先頭は 1 ページ目） */
  const [pages, setPages] = useState<(string | undefined)[]>([undefined]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [composing, setComposing] = useState(false);

  const status = useMailerStatus();
  const connected = status.data?.data.connected ?? false;
  const pageToken = pages[pages.length - 1];

  const params = { folder, q: q || undefined, pageToken, limit: 25 };
  const threads = useQuery({
    queryKey: qk.mailerThreads(params),
    queryFn: () => api.get<ThreadsResponse>('/mailer/threads', params),
    enabled: connected,
    refetchInterval: usePollingInterval(pages.length === 1, 60_000),
  });

  const act = useMutation({
    mutationFn: (input: { threadIds: string[]; action: MailerThreadAction }) =>
      api.post('/mailer/threads/actions', input),
    onSuccess: (_result, input) => {
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['mailer'] });
      const labels: Partial<Record<MailerThreadAction, string>> = {
        archive: 'アーカイブしました',
        trash: 'ゴミ箱に移動しました',
        untrash: '受信トレイに戻しました',
        spam: '迷惑メールにしました',
        notSpam: '迷惑メールではないとしました',
        inbox: '受信トレイに移動しました',
      };
      if (labels[input.action]) toast.success(labels[input.action]!);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const changeFolder = (next: string) => {
    setPages([undefined]);
    setSelected(new Set());
    setSearch('');
    setSearchParams({ folder: next });
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setPages([undefined]);
    setSelected(new Set());
    setSearchParams(search.trim() ? { folder, q: search.trim() } : { folder });
  };

  if (status.isLoading) return <SkeletonRows rows={6} />;
  if (!connected) {
    return (
      <>
        <PageHeader title="メール" />
        <MailerNotConnected />
      </>
    );
  }

  const me = status.data!.data;
  const list = threads.data?.data ?? [];
  const allSelected = list.length > 0 && list.every((t) => selected.has(t.id));
  const selectedIds = [...selected];
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <PageHeader
        title="メール"
        description={me.email ? `${me.email} の受信箱（Gmail）` : undefined}
        actions={
          <Button onClick={() => setComposing(true)}>
            <PenSquare aria-hidden />
            作成
          </Button>
        }
      />

      <div className="flex gap-4">
        {/* ラベル（PC は左に並べ、スマホは選択式） */}
        <nav className="hidden w-48 shrink-0 space-y-0.5 lg:block" aria-label="ラベル">
          {MAILER_FOLDERS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => changeFolder(item.id)}
              className={cn(
                'flex w-full items-center justify-between rounded-control px-3 py-2 text-left text-body',
                folder === item.id ? 'bg-muted font-semibold' : 'hover:bg-muted',
              )}
              aria-current={folder === item.id ? 'page' : undefined}
            >
              <span>{item.label}</span>
              {item.id === 'INBOX' && me.inboxUnread > 0 && (
                <span className="tabular text-note font-semibold">{me.inboxUnread}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={folder}
              onChange={(e) => changeFolder(e.target.value)}
              aria-label="ラベル"
              className="w-auto lg:hidden"
            >
              {MAILER_FOLDERS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                  {item.id === 'INBOX' && me.inboxUnread > 0 ? `（${me.inboxUnread}）` : ''}
                </option>
              ))}
            </Select>
            <form onSubmit={submitSearch} className="relative min-w-0 flex-1" role="search">
              <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground-subtle" />
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="メールを検索（from:相手 has:attachment など Gmail と同じ書き方）"
                aria-label="メールを検索"
                className="pl-9"
              />
            </form>
          </div>

          {/* 一括操作 */}
          <div className="flex min-h-touch flex-wrap items-center gap-1 border-b border-border pb-2 lg:min-h-10">
            <label className="flex items-center px-2" title="すべて選択">
              <input
                type="checkbox"
                className="size-4 accent-foreground"
                checked={allSelected}
                onChange={() => setSelected(allSelected ? new Set() : new Set(list.map((t) => t.id)))}
                aria-label="すべて選択"
              />
            </label>
            {selectedIds.length > 0 ? (
              <>
                {folder === 'TRASH' ? (
                  <BulkButton label="ゴミ箱から戻す" icon={Inbox} onClick={() => act.mutate({ threadIds: selectedIds, action: 'untrash' })} />
                ) : folder === 'SPAM' ? (
                  <BulkButton label="迷惑メールではない" icon={Inbox} onClick={() => act.mutate({ threadIds: selectedIds, action: 'notSpam' })} />
                ) : (
                  <>
                    {folder === 'INBOX' ? (
                      <BulkButton label="アーカイブ" icon={Archive} onClick={() => act.mutate({ threadIds: selectedIds, action: 'archive' })} />
                    ) : (
                      <BulkButton label="受信トレイに移動" icon={Inbox} onClick={() => act.mutate({ threadIds: selectedIds, action: 'inbox' })} />
                    )}
                    <BulkButton label="削除" icon={Trash2} onClick={() => act.mutate({ threadIds: selectedIds, action: 'trash' })} />
                  </>
                )}
                <BulkButton label="既読にする" icon={MailOpen} onClick={() => act.mutate({ threadIds: selectedIds, action: 'read' })} />
                <BulkButton label="未読にする" icon={Mail} onClick={() => act.mutate({ threadIds: selectedIds, action: 'unread' })} />
                <span className="ml-1 text-note text-foreground-muted">{selectedIds.length} 件を選択中</span>
              </>
            ) : (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => void queryClient.invalidateQueries({ queryKey: ['mailer'] })}
                aria-label="更新"
                title="更新"
              >
                <RefreshCw aria-hidden className={cn(threads.isFetching && 'animate-spin')} />
              </Button>
            )}

            <div className="ml-auto flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={pages.length <= 1}
                onClick={() => setPages((prev) => prev.slice(0, -1))}
                aria-label="前のページ"
              >
                <ChevronLeft aria-hidden />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={!threads.data?.meta.nextPageToken}
                onClick={() => setPages((prev) => [...prev, threads.data!.meta.nextPageToken!])}
                aria-label="次のページ"
              >
                <ChevronRight aria-hidden />
              </Button>
            </div>
          </div>

          {threads.isLoading ? (
            <SkeletonRows rows={8} />
          ) : threads.isError ? (
            <EmptyState title="メールを読み込めませんでした" description={errorMessage(threads.error)} />
          ) : list.length === 0 ? (
            <EmptyState
              title={q ? '条件に一致するメールはありません' : 'メールはありません'}
              description={q ? '検索語を変えてお試しください' : undefined}
              icon={<Inbox className="size-8" />}
            />
          ) : (
            <Card className="divide-y divide-border overflow-hidden">
              {list.map((thread) => (
                <ThreadRow
                  key={thread.id}
                  thread={thread}
                  selected={selected.has(thread.id)}
                  onToggle={() => toggle(thread.id)}
                  onOpen={() => navigate(`/mail/${thread.id}?folder=${folder}`)}
                  onStar={() => act.mutate({ threadIds: [thread.id], action: thread.starred ? 'unstar' : 'star' })}
                />
              ))}
            </Card>
          )}
        </div>
      </div>

      {composing && (
        <div className="fixed inset-x-0 bottom-0 z-40 max-h-[92dvh] overflow-y-auto shadow-dialog lg:inset-x-auto lg:right-6 lg:w-full lg:max-w-2xl">
          <Composer
            initial={{ mode: 'new', to: [], cc: [], subject: '', body: withSignature('', me.signature) }}
            signature={me.signature}
            safeModeAllowlist={me.safeModeAllowlist}
            onSent={() => setComposing(false)}
            onDiscard={() => setComposing(false)}
            className="rounded-b-none"
          />
        </div>
      )}
    </>
  );
};

const BulkButton = ({ label, icon: Icon, onClick }: { label: string; icon: typeof Inbox; onClick: () => void }) => (
  <Button variant="ghost" size="icon-sm" onClick={onClick} aria-label={label} title={label}>
    <Icon aria-hidden />
  </Button>
);

const ThreadRow = ({
  thread,
  selected,
  onToggle,
  onOpen,
  onStar,
}: {
  thread: MailerThreadSummary;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onStar: () => void;
}) => (
  <div
    className={cn(
      'flex items-start gap-2 px-2 py-2 lg:items-center',
      thread.unread ? 'bg-background' : 'bg-surface',
      selected && 'bg-muted',
    )}
  >
    <input
      type="checkbox"
      className="mt-1 size-4 shrink-0 accent-foreground lg:mt-0"
      checked={selected}
      onChange={onToggle}
      aria-label={`${thread.subject} を選択`}
    />
    <button
      type="button"
      onClick={onStar}
      className="shrink-0 rounded-control p-1 hover:bg-muted"
      aria-label={thread.starred ? 'スターを外す' : 'スターを付ける'}
      aria-pressed={thread.starred}
    >
      <Star aria-hidden className={cn('size-4', thread.starred ? 'fill-foreground text-foreground' : 'text-foreground-subtle')} />
    </button>
    <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 flex-col gap-0.5 text-left lg:flex-row lg:items-center lg:gap-3">
      <span className={cn('flex min-w-0 items-center gap-1 text-body lg:w-48 lg:shrink-0', thread.unread && 'font-semibold')}>
        <span className="truncate">{thread.participants.join(', ') || '（不明）'}</span>
        {thread.messageCount > 1 && <span className="tabular shrink-0 text-note text-foreground-muted">{thread.messageCount}</span>}
      </span>
      <span className="min-w-0 flex-1 truncate text-body">
        <span className={cn(thread.unread && 'font-semibold')}>{thread.subject}</span>
        {thread.snippet && <span className="text-foreground-muted"> - {thread.snippet}</span>}
      </span>
    </button>
    <span className="flex shrink-0 items-center gap-1 pt-0.5 lg:pt-0">
      {thread.hasAttachment && <Paperclip aria-label="添付あり" className="size-4 text-foreground-muted" />}
      <span className={cn('tabular text-note', thread.unread ? 'font-semibold' : 'text-foreground-muted')}>
        {listDate(thread.lastDate)}
      </span>
    </span>
  </div>
);
